import { generatePKCECodes } from 'src/app/shared/helpers/utility';
import { environment } from 'src/environments/environment';
import { Company } from '../../utility/auth.models';
import { InternalRedirectURI, SSOAction, SSOProvider, SSOState } from './sso.models';

/**
 * SSO redirect URL
 */
export const SSO_REDIRECT_URL = environment.frontEndUrl + '/auth/sso';

/**
 * Tesla SSO constants
 */
const TESLA_APP_ID = 'd06a2f6dc8fe-456e-93e0-e2dffede052b';
const TESLA_SCOPES = [
  'user_data',
  'vehicle_device_data',
  'vehicle_cmds',
  'vehicle_charging_cmds',
  'energy_device_data',
  'energy_cmds',
  'openid',
  'offline_access',
  'vehicle_location',
];
// eslint-disable-next-line max-len
export const TESLA_REVOKE_ACCESS_URL = `https://fleet-auth.prd.vn.cloud.tesla.com/en_au/user/revoke/consent?revoke_client_id=${TESLA_APP_ID}`;

/**
 * Smartcar SSO constants
 */
const SMARTCAR_APP_ID = 'b0fa79eb-2584-46f5-a0a2-8fced1c4c6e6';
const SMARTCAR_SCOPES = [
  'control_charge',
  'control_climate',
  'control_navigation',
  'control_pin',
  'control_security',
  'control_trunk',
  'read_alerts',
  'read_battery',
  'read_charge',
  'read_charge_events',
  'read_charge_locations',
  'read_charge_records',
  'read_climate',
  'read_compass',
  'read_diagnostics',
  'read_engine_oil',
  'read_extended_vehicle_info',
  'read_fuel',
  'read_location',
  'read_odometer',
  'read_security',
  'read_service_history',
  'read_speedometer',
  'read_thermometer',
  'read_tires',
  'read_user_profile',
  'read_vehicle_info',
  'read_vin',
];

/**
 * Volvo SSO constants
 */
const VOLVO_APP_ID = 'dc-du0naj94r02sxemgej3k9ytuu';
const VOLVO_SCOPES = [
  // ----- Connected Vehicle API -----
  'openid',
  'conve:climatization_start_stop',
  'conve:commands',
  'conve:command_accessibility',
  // 'conve:diagnostics_engine_status',
  // 'conve:diagnostics_workshop',
  // 'conve:brake_status',
  // 'conve:windows_status',
  'conve:doors_status',
  'conve:lock_status',
  'conve:lock',
  'conve:unlock',
  'conve:engine_status',
  'conve:engine_start_stop',
  // 'conve:fuel_status',
  // 'conve:battery_charge_level',
  'conve:honk_flash',
  'conve:odometer_status',
  'conve:trip_statistics',
  'conve:tyre_status',
  'conve:vehicle_relation',
  // 'conve:warnings',
  // ----- Energy API -----
  // 'energy:capability:read',
  'energy:state:read',
  // ----- Extended Vehicle API -----
  // 'vehicle:attributes',
  // ----- Location API -----
  'location:read',
];

/**
 * Encodes the internal redirect URI as a dictionary
 * @param {string} internalRedirect - The URI to redirect internally to after SSO is complete
 * @returns {InternalRedirectURI} - The internal redirect URI dictionary
 */
function encodeInternalRedirect(internalRedirect: string): InternalRedirectURI {
  const url = new URL(decodeURIComponent(internalRedirect));
  const params: { [param: string]: string } = {};
  url.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  return {
    path: url.pathname,
    params: params,
  };
}

/**
 * Decodes the internal redirect URI from a dictionary
 * @param {InternalRedirectURI} internalRedirect - The internal redirect URI dictionary
 * @returns {string} - The URI to redirect internally to after SSO is complete
 */
export function decodeInternalRedirect(internalRedirect: InternalRedirectURI): string {
  const url = new URL(internalRedirect.path, window.location.origin);
  for (const key in internalRedirect.params)
    url.searchParams.set(key, internalRedirect.params[key]);
  return url.toString();
}

/**
 * Returns a state object to be passed to the SSO provider
 * @param {SSOProvider} provider - The SSO provider
 * @param {SSOAction} action - The action to perform after SSO is complete
 * @param {string} internalRedirect - The URI to redirect internally to after SSO is complete
 * @param {string} company [optional] - The name of the company this SSO is for
 * @param {string} extra [optional] - Extra params to be passed
 * @returns {string} The SSO redirect URL
 * @author Juan Corral
 */
export function generateSSOState(
  provider: SSOProvider,
  make: string,
  action: SSOAction,
  internalRedirect: string,
  company?: string,
  extra?: string
): SSOState {
  const data: SSOState = {
    provider,
    make,
    action,
    internalRedirect: encodeInternalRedirect(internalRedirect),
  };
  if (company) data.company = company;
  if (extra) data.extra = extra;
  return data;
}

/**
 * Returns an SSO provider's URI
 * @param {SSOProvider} provider - The SSO provider
 * @param {SSOAction} action - The action to perform after SSO is complete
 * @param {string} internalRedirect - The URI to redirect internally to after SSO is complete
 * @param {Company} company [optional] - The company this SSO is for
 * @param {string} extra [optional] - Extra params to be passed
 * @returns {string | undefined} - The SSO Provider URL or undefined if provider is not supported
 * @author Juan Corral
 */
export async function getSSOProviderUri(
  provider: SSOProvider,
  make: string,
  action: SSOAction,
  internalRedirect: string,
  company?: Company,
  extra?: string
): Promise<string> {
  const state = JSON.stringify(
    generateSSOState(provider, make, action, internalRedirect, company?.company_name, extra)
  );

  switch (provider) {
    case SSOProvider.TESLA:
      return Promise.resolve(
        'https://fleet-auth.prd.vn.cloud.tesla.com/oauth2/v3/authorize' +
          '?response_type=code' +
          `&client_id=${TESLA_APP_ID}` +
          '&locale=en-CA' +
          '&prompt=login' +
          `&scope=${TESLA_SCOPES.join(' ')}` +
          '&prompt_missing_scopes=true' +
          '&require_requested_scopes=true' +
          '&show_keypair_step=true' +
          `&state=${state}` +
          `&redirect_uri=${encodeURIComponent(SSO_REDIRECT_URL)}`
      );
    case SSOProvider.SMARTCAR: {
      return Promise.resolve(
        'https://connect.smartcar.com/oauth/authorize' +
          '?response_type=code' +
          `&client_id=${SMARTCAR_APP_ID}` +
          `&scope=${SMARTCAR_SCOPES.join(' ')}` +
          `&redirect_uri=${encodeURIComponent(SSO_REDIRECT_URL)}` +
          `&state=${state}` +
          '&mode=live'
      );
    }
    case SSOProvider.VOLVO: {
      const { verifier, challenge } = await generatePKCECodes();
      sessionStorage.setItem('pkce_verifier', verifier);
      return Promise.resolve(
        'https://volvoid.eu.volvocars.com/as/authorization.oauth2' +
          '?response_type=code' +
          `&client_id=${VOLVO_APP_ID}` +
          `&scope=${encodeURIComponent(VOLVO_SCOPES.join(' '))}` +
          `&state=${state}` +
          `&redirect_uri=${encodeURIComponent(SSO_REDIRECT_URL)}` +
          `&code_challenge=${challenge}` +
          '&code_challenge_method=S256'
      );
    }
    default:
      throw new Error(`Unsupported SSO provider: ${provider as string}`);
  }
}
